import { createContext, type ReactNode, useCallback, useContext, useState } from 'react'

interface SelectedClient {
  id: string
  name: string
}

interface SelectedApp {
  id: string
  name: string
  accesses: string[]
}

interface SelectedAppContextValue {
  selectedClient: SelectedClient | null
  selectedApp: SelectedApp | null
  setSelectedApp: (client: SelectedClient, app: SelectedApp) => void
  clearSelectedApp: () => void
}

const SelectedAppContext = createContext<SelectedAppContextValue | null>(null)

const CLIENT_KEY = 'mobifull_client'
const APP_KEY = 'mobifull_app'

export const SelectedAppProvider = ({ children }: { children: ReactNode }) => {
  const [selectedClient, setSelectedClient] = useState<SelectedClient | null>(() => {
    try {
      const raw = localStorage.getItem(CLIENT_KEY)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })
  const [selectedApp, setSelectedAppState] = useState<SelectedApp | null>(() => {
    try {
      const raw = localStorage.getItem(APP_KEY)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  const setSelectedApp = useCallback((client: SelectedClient, app: SelectedApp) => {
    localStorage.setItem(CLIENT_KEY, JSON.stringify(client))
    localStorage.setItem(APP_KEY, JSON.stringify(app))
    setSelectedClient(client)
    setSelectedAppState(app)
  }, [])

  const clearSelectedApp = useCallback(() => {
    localStorage.removeItem(CLIENT_KEY)
    localStorage.removeItem(APP_KEY)
    setSelectedClient(null)
    setSelectedAppState(null)
  }, [])

  return (
    <SelectedAppContext.Provider
      value={{ selectedClient, selectedApp, setSelectedApp, clearSelectedApp }}>
      {children}
    </SelectedAppContext.Provider>
  )
}

export const useSelectedApp = () => {
  const ctx = useContext(SelectedAppContext)
  if (!ctx) {
    throw new Error('useSelectedApp must be used within SelectedAppProvider')
  }
  return ctx
}
